import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { SubscriberBaseDirective } from '@app/shared/components/subscriber-base/subscriber-base.directive';
import { NotifierService } from '@app/shared/services/notifier.service';
import { TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs';
import { finalize, mergeMap } from 'rxjs/operators';
import { isValidFileExtension, isValidFileSize } from '../uploader';
import { UploaderService } from '../uploader.service';

export type ImageUploaderTemplateOptions = 'profile-pic' | 'compact' | 'full';
/**
 *
 * @example
 * ```
 *  <dgx-image-uploader
 *    #fileUploader
 *    [showPlaceholder]="!imageUrl"
 *    templateOption="compact"
 *    uploadApiEndpoint="/inputs/uploadpicture"
 *    (uploadSuccessEvent)="onImageChanged($event)"
 *    (imageRemovedEvent)="onImageRemoved()"
 *  >
 *    <div class="original-preview-image-parent">
 *      <img class="stretch" [src]="imageUrl" />
 *    </div>
 *  </dgx-image-uploader>
 * ```
 */
@Component({
    selector: 'dgx-image-uploader',
    templateUrl: './image-uploader.component.html',
    styleUrls: ['./image-uploader.component.scss'],
    standalone: false
})
export class ImageUploaderComponent
  extends SubscriberBaseDirective
  implements OnInit
{
  @Input() public isFullWidthImage?: boolean = false;
  /** Upload api endpoint. Required when a custom uploadFile function is not set.  */
  @Input() public uploadApiEndpoint: string;
  /** Optional function to prepare the image file before upload. Will not be used if uploadFile is set.  */
  @Input() public prepareFile: (file: File) => FormData;
  /** Optional function to handle the entire image file upload. */
  @Input() public uploadFile: (file: File) => Observable<any>;
  /** When true, the placeholder, based on the template option, will be shown . */
  @Input() public showPlaceholder?: boolean = true;
  /** Override accepted file extensions */
  @Input() public acceptedExtensions?: string[];
  @Input() public templateOption?: ImageUploaderTemplateOptions = 'full';
  /** Optional id to be used for file input's aria-describedBy attribute. */
  @Input() public imageUploaderDescId?: string = undefined;
  @Output() public uploadSuccessEvent?: EventEmitter<any> = new EventEmitter();
  @Output() public imageRemovedEvent?: EventEmitter<any> = new EventEmitter();
  @Output() public uploadErrorEvent?: EventEmitter<any> = new EventEmitter();
  @ViewChild('inputRef') public inputRef: ElementRef;
  public i18n = this.translateService.instant([
    'dgImageEdit_ChangePhoto',
    'dgImageEdit_NoImage',
    'dgImageEdit_Change',
    'dgImageEdit_DragPhoto',
    'Core_Or',
  ]);
  public uploaderFocused: boolean;
  public maxImageSizeMB: number;
  public allowedFileTypes: string[];
  public isUploading: boolean = false;
  constructor(
    private translateService: TranslateService,
    private uploaderService: UploaderService,
    private notifierService: NotifierService,
    private cdr: ChangeDetectorRef
  ) {
    super();
  }

  public ngOnInit() {
    if (!this.uploadFile && !this.uploadApiEndpoint) {
      console.warn('Image uploader needs uploadFile or uploadApiEndpoint set');
    }

    this.prepareFile = this.prepareFile ?? this.defaultPrepareFile;
    this.uploadFile = this.uploadFile ?? this.defaultUploadFile;
    this.uploaderService
      .getUploadLimit('image')
      .pipe(this.takeUntilDestroyed())
      .subscribe((results) => {
        this.maxImageSizeMB = results.maxSizeMB;
        this.allowedFileTypes =
          this.acceptedExtensions ?? results.allowedFileTypes;
      });
  }

  public onImageSelected(event) {
    const files = event.target ? event.target.files : event;
    const file = files.item(0);
    if (file && this.isValid(file)) {
      this.isUploading = true;
      this.uploadFile(file)
        .pipe(
          finalize(() => {
            this.isUploading = false;
            this.cdr.detectChanges();
          })
        )
        .subscribe(
          (response) => {
            this.inputRef.nativeElement.value = '';
            if (response?.error) {
              this.onError(response.error);
            } else {
              this.uploadSuccessEvent.emit(response);
            }
          },
          (error) => {
            this.inputRef.nativeElement.value = '';
            this.onError(error);
          }
        );
    }
  }

  public removeImage() {
    this.inputRef.nativeElement.value = '';
    this.imageRemovedEvent.emit();
  }

  public defaultUploadFile(file: File) {
    return this.uploaderService.getImageDimension(file).pipe(
      mergeMap(({ width, height }) => {
        const preparedFile = this.prepareFile(file);
        return this.uploaderService.uploadFile(
          preparedFile,
          this.uploadApiEndpoint,
          {},
          {
            pointY: 0,
            pointX: 0,
            width: width,
            height: height,
          }
        );
      })
    );
  }

  public defaultPrepareFile(file: File) {
    return this.uploaderService.prepRawImageFile(file);
  }

  public onError(error) {
    this.uploadErrorEvent.emit(error);
    console.error(error);
    this.notifierService.showError(
      this.translateService.instant('ImageEditSvc_ImageUploadError')
    );
  }

  public onFocus(): void {
    this.uploaderFocused = true;
  }

  public onBlur(): void {
    this.uploaderFocused = false;
  }

  public onChangeDragState($event) {
    switch ($event) {
      case 'dragover':
        this.uploaderFocused = true;
        break;
      default:
        this.uploaderFocused = false;
    }
  }

  private isValid(file: File) {
    const isValidSize = isValidFileSize(file, this.maxImageSizeMB);
    const isValidExtension = isValidFileExtension(file, this.allowedFileTypes);
    if (!isValidSize) {
      this.notifierService.showError(
        this.translateService.instant('dgImageUpload_FilesizeError', {
          maxSize: this.maxImageSizeMB + 'MB',
        })
      );
    }

    if (!isValidExtension) {
      this.notifierService.showError(
        this.translateService.instant('dgImageUpload_FileTypes', {
          imageTypes: this.allowedFileTypes.join(', '),
        })
      );
    }

    return isValidExtension && isValidSize;
  }
}
